import React from 'react';
import { Plane } from 'lucide-react';

/**
 * Shared presentational timeline visuals.
 *
 * These are the visual cores of the exec-dashboard timeline cards
 * (ExecDashboardV2 FlightCard etc.), extracted as pure display components so
 * the trip timeline (Timeline2 / TimelineComponentCard "Cards" view mode) can
 * render the same look while keeping its own shell - drag-to-reorder, FIXED
 * anchoring, VIP greet, edit/delete actions.
 *
 * They take plain display strings only (no component/leg objects, no
 * handlers) so both pages can map their own data shapes onto them.
 */

const normalizeTerminal = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return 'TBD';
  return String(value).trim().replace(/^terminal\s*/i, '').replace(/^t(?=\d)/i, '') || 'TBD';
};

const normalizeGate = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') return 'TBD';
  return String(value).trim().replace(/^gate\s*/i, '') || 'TBD';
};

/**
 * Operational flight details shared by executive and trip timelines.
 * `trailing` renders right-aligned on the aircraft row (the fare/bag icons).
 * Commercial flights keep terminal/gate placeholders visible because those
 * assignments often arrive shortly before departure. Private flights can opt
 * out of airport details while still displaying a scheduled aircraft type.
 */
const cleanOptional = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

// A clock time ("12:55 AM" or "00:55") kept on one line, with AM/PM set
// smaller so 12-hour times fit the same columns as 24-hour ones.
export const ClockTime = ({ value, fallback = '--:--' }) => {
  const match = String(value || '').match(/^(.*?)\s*(AM|PM)$/i);
  if (!value) return fallback;
  if (!match) return <span className="whitespace-nowrap">{value}</span>;
  return (
    <span className="whitespace-nowrap">
      {match[1]}<span className="ml-1 text-[0.62em] font-medium tracking-wide">{match[2].toUpperCase()}</span>
    </span>
  );
};

export const FlightOperationalInfo = ({
  departureTerminal,
  departureGate,
  arrivalTerminal,
  arrivalGate,
  departureCheckIn,
  arrivalBaggageClaim,
  aircraftType,
  showAirportDetails = true,
  trailing = null,
}) => {
  const aircraft = cleanOptional(aircraftType);
  const checkIn = cleanOptional(departureCheckIn);
  const baggageClaim = cleanOptional(arrivalBaggageClaim);

  if (!showAirportDetails && !aircraft && !trailing) return null;

  const labelClass = 'text-[11px] font-semibold uppercase tracking-wide text-ui-muted';
  const valueClass = 'text-xs sm:text-sm font-medium text-ui-ink';
  const secondaryClass = 'text-xs text-ui-ink-3';

  const renderAirportDetails = (terminal, gate, extraLabel, extraValue) => (
    <>
      <div className={valueClass}>
        <span>Terminal {normalizeTerminal(terminal)}</span>
        <span className="text-ui-faint"> • </span>
        <span>Gate {normalizeGate(gate)}</span>
      </div>
      {extraValue && (
        <div className={secondaryClass}>{extraLabel} {extraValue}</div>
      )}
    </>
  );

  return (
    <div className="mt-3 pt-3 border-t border-ui-divider">
      {showAirportDetails && (
        <div className="grid grid-cols-2 gap-3">
          <div className="min-w-0">
            <div className={labelClass}>Departure</div>
            {renderAirportDetails(departureTerminal, departureGate, 'Check-in', checkIn)}
          </div>
          <div className="min-w-0 text-right">
            <div className={labelClass}>Arrival</div>
            {renderAirportDetails(arrivalTerminal, arrivalGate, 'Baggage', baggageClaim)}
          </div>
        </div>
      )}
      {(aircraft || trailing) && (
        <div className={`${showAirportDetails ? 'mt-2' : ''} flex items-center justify-between gap-2`}>
          {aircraft ? (
            <div className="flex items-baseline gap-2 min-w-0">
              <span className={labelClass}>Aircraft</span>
              <span className={valueClass}>{aircraft}</span>
            </div>
          ) : <span />}
          {trailing}
        </div>
      )}
    </div>
  );
};

/**
 * Exec-style flight route row: big airport codes with names and times on each
 * side, a gradient flight path with the airline code + flight number (or tail
 * number for private flights) underneath.
 *
 * Props:
 * - dep / arr: { code, name, sub, time, scheduledTime } - sub is an optional
 *   secondary line (e.g. FBO name for private flights); scheduledTime renders
 *   struck-through under time when it differs (delay display).
 * - chip: short bold label on the path (airline IATA code or tail number)
 * - chipSub: text next to the chip (flight number)
 * - isPrivate: smaller code type scale + violet chip (tail number)
 * - dayDiff: positive integer renders a "+N" overnight badge next to arr.time
 */
export const FlightRouteVisual = ({
  dep = {},
  arr = {},
  chip,
  chipSub,
  isPrivate = false,
  dayDiff = 0,
}) => {
  const codeClass = `${isPrivate ? 'text-xl sm:text-2xl' : 'text-2xl sm:text-3xl'} font-bold tracking-tight text-ui-ink`;
  const nameClass = 'text-xs sm:text-sm truncate text-ui-ink-3';
  const subClass = 'text-xs truncate text-ui-amber-fg';
  const timeClass = 'text-base sm:text-lg font-semibold text-ui-ink';
  const scheduledClass = 'text-xs line-through text-ui-muted';
  const lineClass = 'from-ui-line to-ui-blue-strong';
  const chipClass = isPrivate
    ? 'text-[10px] sm:text-xs font-bold px-1 sm:px-1.5 py-0.5 rounded text-ui-violet-fg bg-ui-violet-bg'
    : 'text-[10px] sm:text-xs font-bold px-1 sm:px-1.5 py-0.5 rounded text-ui-blue-fg bg-ui-blue-bg';

  const renderEndpoint = (endpoint, alignRight = false) => (
    <div className={`flex-1 min-w-0 ${alignRight ? 'text-right' : ''}`}>
      <div className={codeClass}>{endpoint.code || 'TBD'}</div>
      {endpoint.name && <div className={nameClass}>{endpoint.name}</div>}
      {endpoint.sub && <div className={subClass} title={endpoint.sub}>{endpoint.sub}</div>}
      <div className="mt-1">
        <div className={timeClass}>
          {endpoint.time || '--:--'}
          {alignRight && dayDiff !== 0 && (
            <span className="ml-1 text-xs px-1.5 py-0.5 rounded font-medium align-middle bg-ui-amber-bg text-ui-amber-fg">
              {dayDiff > 0 ? '+' : ''}{dayDiff}
            </span>
          )}
        </div>
        {endpoint.scheduledTime && endpoint.scheduledTime !== endpoint.time && (
          <div className={scheduledClass}>{endpoint.scheduledTime}</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex items-center gap-2 sm:gap-4">
      {renderEndpoint(dep)}

      {/* Flight path indicator */}
      <div className="flex-shrink-0 w-16 sm:w-24 flex flex-col items-center">
        <div className="w-full flex items-center gap-1 sm:gap-2">
          <div className={`h-px flex-1 bg-gradient-to-r ${lineClass}`} />
          <div className={`p-1.5 sm:p-2 rounded-full bg-gradient-to-br ${
            isPrivate ? 'from-ui-violet-strong to-ui-indigo-strong' : 'from-ui-blue-strong to-ui-teal-strong'
          }`}>
            <Plane className="w-3 h-3 sm:w-4 sm:h-4 text-ui-on-primary rotate-90" />
          </div>
          <div className={`h-px flex-1 bg-gradient-to-r ${lineClass}`} style={{ transform: 'scaleX(-1)' }} />
        </div>
        {(chip || chipSub) && (
          <div className="flex items-center gap-1 mt-1 sm:mt-2">
            {chip && <span className={chipClass}>{chip}</span>}
            {chipSub && (
              <span className="text-[10px] sm:text-xs font-medium truncate max-w-[48px] sm:max-w-none text-ui-muted">
                {chipSub}
              </span>
            )}
          </div>
        )}
      </div>

      {renderEndpoint(arr, true)}
    </div>
  );
};

export default FlightRouteVisual;
